import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminShell } from "../components/AdminShell";
import { api, imageUrl } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, X, ImagePlus, Sliders, Wand2, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { AdminWhatsappShareModal } from "../components/AdminWhatsappShareModal";

const FUELS = ["Benzina", "Diesel", "Ibrida", "Elettrica", "GPL", "Metano"];
const TRANSMISSIONS = [
  { value: "Manuale", label: "Manuale" },
  { value: "Automatico", label: "Automatico" },
  { value: "Semi-automatico", label: "Semi-automatico", indent: true },
  { value: "Robotizzato", label: "Robotizzato", indent: true },
];
const BODY_TYPES = ["City Car", "Berlina", "Station Wagon", "SUV", "Coupé", "Cabrio", "Monovolume", "Minibus"];
const CAR_COLORS = [
  "Bianco", "Nero", "Grigio", "Argento", "Antracite",
  "Blu", "Azzurro", "Rosso", "Bordeaux", "Verde",
  "Verde scuro", "Giallo", "Arancione", "Marrone", "Beige",
  "Oro", "Bronzo", "Viola", "Rosa", "Champagne",
];

// Catalogo optional Guzzardella Auto - raggruppato per categoria per UX pulita.
const OPTIONAL_CATALOG = [
  {
    category: "Cambio & Trasmissione",
    items: [
      "Cambio Manuale",
      "Cambio Semiautomatico",
      "Cambio Automatico",
      "Rapporti 5",
      "Rapporti 6",
      "Rapporti 7",
      "Rapporti 8",
      "Rapporti 9",
    ],
  },
  {
    category: "Comfort",
    items: [
      "Climatizzatore manuale",
      "Climatizzatore automatico bi-zona",
      "Climatizzatore automatico tri-zona",
      "Sedili riscaldati",
      "Sedili ventilati",
      "Sedili elettrici",
      "Sedili in pelle",
      "Sedili sportivi",
      "Volante multifunzione",
      "Volante riscaldato",
      "Cruise control",
      "Cruise control adattivo",
      "Keyless entry & start",
      "Specchietti elettrici riscaldati",
      "Bracciolo centrale",
    ],
  },
  {
    category: "Sicurezza",
    items: [
      "ABS",
      "ESP / Controllo stabilità",
      "Airbag frontali",
      "Airbag laterali",
      "Airbag a tendina",
      "Sensori parcheggio anteriori",
      "Sensori parcheggio posteriori",
      "Telecamera posteriore",
      "Telecamera 360°",
      "Frenata automatica di emergenza",
      "Mantenimento corsia",
      "Rilevamento angolo cieco",
      "Riconoscimento segnali stradali",
      "Fari LED",
      "Fari Xenon",
      "Fari Matrix / Adaptive",
      "Fendinebbia",
      "Luci diurne LED",
      "Isofix",
    ],
  },
  {
    category: "Multimedia",
    items: [
      "Radio DAB",
      "Bluetooth",
      "Apple CarPlay",
      "Android Auto",
      "Sistema di navigazione",
      "Schermo touch",
      "Presa USB",
      "Ricarica wireless",
      "Impianto audio premium",
      "Comandi vocali",
    ],
  },
  {
    category: "Estetici & Extra",
    items: [
      "Cerchi in lega",
      "Vetri privacy oscurati",
      "Tetto apribile",
      "Tetto panoramico",
      "Barre longitudinali sul tetto",
      "Gancio traino",
      "Verniciatura metallizzata",
      "Head-up display",
      "Sistema Start&Stop",
      "Modalità di guida selezionabili",
      "Sensore pioggia",
      "Sensore crepuscolare",
    ],
  },
];

const CAR_BRANDS = [
  "Abarth", "Alfa Romeo", "Audi", "BMW", "Chevrolet", "Citroën", "Cupra", "Dacia", "DS",
  "Fiat", "Ford", "Honda", "Hyundai", "Jaguar", "Jeep", "Kia", "Lancia", "Land Rover",
  "Lexus", "Mazda", "Mercedes-Benz", "MG", "Mini", "Mitsubishi", "Nissan", "Opel",
  "Peugeot", "Polestar", "Porsche", "Range Rover", "Renault", "Seat", "Skoda", "Smart",
  "SsangYong", "Subaru", "Suzuki", "Tesla", "Toyota", "Volkswagen", "Volvo",
];

const POPULAR_MODELS = [
  // Fiat
  "500", "500L", "500X", "500e", "Panda", "Punto", "Tipo", "Bravo", "Doblò", "Ducato",
  // Alfa Romeo
  "Giulia", "Giulietta", "Stelvio", "Tonale", "MiTo",
  // Volkswagen
  "Polo", "Golf", "Passat", "Tiguan", "T-Roc", "T-Cross", "Touran", "ID.3", "ID.4",
  // Audi
  "A1", "A3", "A4", "A5", "A6", "Q2", "Q3", "Q5", "Q7", "Q8", "e-tron",
  // BMW
  "Serie 1", "Serie 2", "Serie 3", "Serie 4", "Serie 5", "X1", "X2", "X3", "X4", "X5", "iX",
  // Mercedes
  "Classe A", "Classe B", "Classe C", "Classe E", "Classe S", "GLA", "GLB", "GLC", "GLE", "EQC",
  // Renault
  "Clio", "Captur", "Megane", "Kadjar", "Scenic", "Zoe", "Twingo",
  // Peugeot
  "208", "2008", "308", "3008", "508", "5008", "Rifter",
  // Citroen
  "C3", "C4", "C5", "Berlingo", "C3 Aircross", "C5 Aircross",
  // Opel
  "Corsa", "Astra", "Insignia", "Mokka", "Crossland", "Grandland", "Zafira",
  // Ford
  "Fiesta", "Focus", "Puma", "Kuga", "EcoSport", "Transit", "Ranger",
  // Toyota
  "Yaris", "Corolla", "Aygo", "C-HR", "RAV4", "Hilux", "Prius",
  // Dacia
  "Sandero", "Duster", "Logan", "Spring", "Jogger",
  // Skoda
  "Octavia", "Fabia", "Superb", "Kamiq", "Karoq", "Kodiaq", "Scala",
  // Seat / Cupra
  "Ibiza", "Leon", "Ateca", "Arona", "Tarraco", "Formentor",
  // Hyundai / Kia
  "Tucson", "Kona", "i10", "i20", "i30", "IONIQ 5", "Sportage", "Picanto", "Ceed", "Sorento", "EV6", "Niro",
  // Nissan
  "Qashqai", "Juke", "Micra", "X-Trail", "Leaf", "Ariya",
  // Tesla
  "Model 3", "Model Y", "Model S", "Model X",
  // Mini / Smart
  "Cooper", "Countryman", "Clubman", "Fortwo", "Forfour",
  // Jeep
  "Renegade", "Compass", "Wrangler", "Grand Cherokee", "Avenger",
];

export default function AdminCarForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    price: "",
    km: "",
    fuel: "",
    transmission: "",
    color: "",
    color_finish: "",
    power_hp: "",
    power_kw: "",
    engine_cc: "",
    body_type: "",
    youtube_url: "",
    optionals: [],
    description: "",
    is_sold: false,
    is_reserved: false,
    is_on_sale: false,
    sale_label: "",
    sale_price: "",
    notify_customers: true,
  });
  const [images, setImages] = useState([]);
  const [carId, setCarId] = useState(id || null);
  const [loading, setLoading] = useState(false);
  const [aiDescriptionLoading, setAiDescriptionLoading] = useState(false);
  const editing_snapshot_ref = useRef(false);
  const [storyVideoPath, setStoryVideoPath] = useState(null);
  const [storyVideoUploading, setStoryVideoUploading] = useState(false);
  const [waShareOpen, setWaShareOpen] = useState(false);
  const [waShareCar, setWaShareCar] = useState(null);

  const uploadStoryVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !carId) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Video troppo grande (max 25MB)");
      return;
    }
    setStoryVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/cars/${carId}/story-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStoryVideoPath(r.data.story_video_path);
      toast.success("Video caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload video");
    } finally {
      setStoryVideoUploading(false);
      e.target.value = "";
    }
  };

  const removeStoryVideo = async () => {
    if (!carId) return;
    try {
      await api.delete(`/cars/${carId}/story-video`);
      setStoryVideoPath(null);
      toast.success("Video rimosso");
    } catch { toast.error("Errore rimozione"); }
  };

  const generateAiDescription = async () => {
    if (!form.brand || !form.model) {
      toast.error("Compila almeno marca e modello");
      return;
    }
    setAiDescriptionLoading(true);
    try {
      const r = await api.post("/cars/generate-description", {
        brand: form.brand,
        model: form.model,
        year: form.year,
        km: form.km !== "" ? parseInt(form.km) : null,
        fuel: form.fuel,
        transmission: form.transmission,
        body_type: form.body_type,
        color: form.color,
        power_hp: form.power_hp !== "" ? parseInt(form.power_hp) : null,
        optionals: form.optionals,
      });
      setForm((f) => ({ ...f, description: r.data.description }));
      toast.success("Descrizione generata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione descrizione");
    } finally {
      setAiDescriptionLoading(false);
    }
  };

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const r = await api.get(`/cars/${id}`);
        setForm({
          brand: r.data.brand,
          model: r.data.model,
          year: r.data.year,
          price: r.data.price,
          km: r.data.km,
          fuel: r.data.fuel || "",
          transmission: r.data.transmission || "",
          color: r.data.color || "",
          color_finish: r.data.color_finish || "",
          power_hp: r.data.power_hp ?? "",
          power_kw: r.data.power_kw ?? "",
          engine_cc: r.data.engine_cc ?? "",
          body_type: r.data.body_type || "",
          youtube_url: r.data.youtube_url || "",
          optionals: Array.isArray(r.data.optionals) ? r.data.optionals : [],
          description: r.data.description || "",
          is_sold: r.data.is_sold,
          is_reserved: r.data.is_reserved || false,
          is_on_sale: r.data.is_on_sale || false,
          sale_label: r.data.sale_label || "",
          sale_price: r.data.sale_price ?? "",
          is_featured: !!r.data.is_featured,
          featured_order: r.data.featured_order ?? 0,
        });
        setImages(r.data.images || []);
        setStoryVideoPath(r.data.story_video_path || null);
        editing_snapshot_ref.current = !!r.data.is_on_sale;
      } catch { toast.error("Auto non trovata"); nav("/admin/cars"); }
    })();
  }, [id]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.year || !form.price || form.km === "") {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      year: parseInt(form.year),
      price: parseFloat(form.price),
      km: parseInt(form.km),
      power_hp: form.power_hp !== "" ? parseInt(form.power_hp) : null,
      power_kw: form.power_kw !== "" ? parseInt(form.power_kw) : null,
      engine_cc: form.engine_cc !== "" ? parseInt(form.engine_cc) : null,
      optionals: form.optionals,
      featured_order: form.featured_order !== "" ? parseInt(form.featured_order) || 0 : 0,
      sale_price: form.is_on_sale && form.sale_price !== "" ? parseFloat(form.sale_price) : null,
      sale_label: form.is_on_sale ? (form.sale_label || "Promozione") : null,
    };
    try {
      if (editing) {
        const wasPromo = !!editing_snapshot_ref.current;
        const url = form.is_on_sale && !wasPromo && form.notify_customers ? `/cars/${id}?notify=true` : `/cars/${id}`;
        const r = await api.put(url, payload);
        const notif = r.data._notify;
        if (notif) {
          const en = notif.email?.sent || 0;
          const pn = notif.push?.sent || 0;
          toast.success(`Auto aggiornata. Notificati ${en} email + ${pn} push.`);
        } else {
          toast.success("Auto aggiornata");
        }
        editing_snapshot_ref.current = r.data.is_on_sale;
      } else {
        const r = await api.post(`/cars${form.notify_customers ? "?notify=true" : ""}`, payload);
        setCarId(r.data.id);
        setWaShareCar(r.data);
        setWaShareOpen(true);
        const notif = r.data.notification;
        if (form.notify_customers && notif) {
          const emailN = notif.email?.sent || 0;
          const emailT = notif.email?.total || 0;
          const pushN = notif.push?.sent || 0;
          const pushT = notif.push?.total || 0;
          toast.success(`Auto creata. Notificati ${emailN}/${emailT} email · ${pushN}/${pushT} push.`);
        } else {
          toast.success("Auto creata. Aggiungi ora le foto.");
        }
        nav(`/admin/cars/${r.data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setLoading(false); }
  };

  const uploadImage = async (file) => {
    if (!carId) {
      toast.error("Salva prima l'auto");
      return;
    }
    if (images.length >= 20) {
      toast.error("Massimo 20 foto per auto");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/cars/${carId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages((imgs) => [...imgs, r.data]);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  };

  const handleFiles = async (files) => {
    for (const f of Array.from(files)) {
      await uploadImage(f);
    }
  };

  const removeImage = async (imgId) => {
    try {
      await api.delete(`/cars/${carId}/images/${imgId}`);
      setImages((imgs) => imgs.filter((i) => i.id !== imgId));
      toast.success("Foto rimossa");
    } catch { toast.error("Errore rimozione"); }
  };

  return (
    <AdminShell title={editing ? "Modifica Auto" : "Nuova Auto"}>
      <Link
        to="/admin/cars"
        className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-gold mb-6"
        data-testid="back-to-cars"
      >
        <ArrowLeft size={14} /> Torna al parco auto
      </Link>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="car-form">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-midnight-light border border-white/5 p-6">
            <h3 className="font-display text-lg font-semibold mb-6 text-gold">Dati principali</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Marca *</Label>
                <Input
                  required
                  value={form.brand}
                  onChange={update("brand")}
                  list="car-brands-list"
                  placeholder="Inizia a digitare o scegli..."
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="form-brand"
                />
                <datalist id="car-brands-list">
                  {CAR_BRANDS.map((b) => (
                    <option key={b} value={b} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Modello *</Label>
                <Input
                  required
                  value={form.model}
                  onChange={update("model")}
                  list="car-models-list"
                  placeholder="Inizia a digitare o scegli..."
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="form-model"
                />
                <datalist id="car-models-list">
                  {POPULAR_MODELS.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Anno *</Label>
                <Input required type="number" value={form.year} onChange={update("year")} className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-year" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Prezzo (€) *</Label>
                <Input required type="number" step="100" value={form.price} onChange={update("price")} className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-price" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Chilometraggio *</Label>
                <Input required type="number" value={form.km} onChange={update("km")} className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-km" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Colore</Label>
                <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-color">
                    <SelectValue placeholder="Seleziona colore..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CAR_COLORS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Finitura</Label>
                <Select value={form.color_finish} onValueChange={(v) => setForm({ ...form, color_finish: v })}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-color-finish">
                    <SelectValue placeholder="Seleziona finitura..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="metallizzato">Metallizzato</SelectItem>
                    <SelectItem value="perlato">Perlato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Alimentazione</Label>
                <Select value={form.fuel} onValueChange={(v) => setForm({ ...form, fuel: v })}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-fuel">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {FUELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Cambio</Label>
                <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-transmission">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSMISSIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value} className={t.indent ? "pl-10" : ""}>
                        {t.indent ? `↳ ${t.label}` : t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Potenza (CV)</Label>
                <Input type="number" value={form.power_hp} onChange={update("power_hp")} placeholder="Es. 150" className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-power-hp" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Potenza (kW)</Label>
                <Input type="number" value={form.power_kw} onChange={update("power_kw")} placeholder="Es. 110" className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-power-kw" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Cilindrata (cc)</Label>
                <Input type="number" value={form.engine_cc} onChange={update("engine_cc")} placeholder="Es. 1998" className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-engine-cc" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Tipologia carrozzeria</Label>
                <Select value={form.body_type} onValueChange={(v) => setForm({ ...form, body_type: v })}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 rounded-sm text-white" data-testid="form-body-type">
                    <SelectValue placeholder="Seleziona tipologia..." />
                  </SelectTrigger>
                  <SelectContent>
                    {BODY_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Link Video YouTube</Label>
                <Input
                  value={form.youtube_url}
                  onChange={update("youtube_url")}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white font-mono text-xs"
                  data-testid="form-youtube-url"
                />
                <p className="text-[10px] text-white/40 mt-2">
                  Opzionale. Se compilato, il video verrà mostrato sulla scheda auto pubblica.
                  Formati accettati: youtube.com/watch?v=ID · youtu.be/ID · youtube.com/embed/ID
                </p>
              </div>
            </div>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Sliders size={18} className="text-gold" />
              <h3 className="font-display text-lg font-semibold text-gold">Optional & Dotazione</h3>
              <span className="ml-auto text-xs text-white/40">
                {form.optionals.length} selezionati
              </span>
            </div>
            <p className="text-xs text-white/50 mb-5 leading-relaxed">
              Spunta gli optional presenti su questa vettura. Verranno mostrati al cliente nella pagina dettaglio.
            </p>
            <div className="space-y-6">
              {OPTIONAL_CATALOG.map((group) => (
                <div key={group.category}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs uppercase tracking-widest text-gold/80 font-semibold">
                      {group.category}
                    </h4>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          const missing = group.items.filter((i) => !form.optionals.includes(i));
                          setForm({ ...form, optionals: [...form.optionals, ...missing] });
                        }}
                        className="text-[10px] uppercase tracking-widest text-white/50 hover:text-gold"
                        data-testid={`select-all-${group.category.toLowerCase().replace(/\s|&/g, "")}`}
                      >
                        Tutti
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setForm({
                            ...form,
                            optionals: form.optionals.filter((o) => !group.items.includes(o)),
                          });
                        }}
                        className="text-[10px] uppercase tracking-widest text-white/50 hover:text-gold"
                        data-testid={`clear-${group.category.toLowerCase().replace(/\s|&/g, "")}`}
                      >
                        Nessuno
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {group.items.map((item) => {
                      const checked = form.optionals.includes(item);
                      return (
                        <label
                          key={item}
                          className={`flex items-center gap-2 py-2 px-3 border cursor-pointer transition-colors text-sm ${
                            checked
                              ? "bg-gold/10 border-gold/60 text-white"
                              : "bg-white/5 border-white/10 text-white/70 hover:border-gold/40"
                          }`}
                          data-testid={`optional-${item.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <input
                            type="checkbox"
                            className="accent-[#D4AF37] w-3.5 h-3.5"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setForm({ ...form, optionals: [...form.optionals, item] });
                              } else {
                                setForm({
                                  ...form,
                                  optionals: form.optionals.filter((o) => o !== item),
                                });
                              }
                            }}
                          />
                          <span className="leading-tight">{item}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6" data-testid="story-video-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-semibold text-gold">Video Story</h3>
                <p className="text-[11px] text-white/40 mt-1">Video verticale 15-30s (mp4/webm/mov, max 25MB) — autoplay muto sulla scheda auto.</p>
              </div>
              {!editing ? (
                <span className="text-[10px] uppercase tracking-widest text-white/40">Salva prima l'auto</span>
              ) : storyVideoPath ? (
                <button
                  type="button"
                  onClick={removeStoryVideo}
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white rounded-sm"
                  data-testid="story-video-remove"
                >
                  Rimuovi
                </button>
              ) : null}
            </div>
            {editing && (
              <label className={`block border-2 border-dashed p-6 text-center cursor-pointer rounded-sm transition-colors ${storyVideoUploading ? "opacity-50 pointer-events-none border-white/20" : storyVideoPath ? "border-gold/40 bg-gold/5" : "border-white/20 hover:border-gold"}`}>
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  onChange={uploadStoryVideo}
                  data-testid="story-video-input"
                />
                <span className="text-sm text-gold uppercase tracking-widest font-semibold">
                  {storyVideoUploading ? "Carico..." : storyVideoPath ? "Sostituisci video" : "Carica video"}
                </span>
                <p className="text-[10px] text-white/40 mt-2">
                  {storyVideoPath ? "Video attualmente caricato ✓" : "Trascina o clicca per selezionare"}
                </p>
              </label>
            )}
          </div>

          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-gold">Descrizione</h3>
              <button
                type="button"
                onClick={generateAiDescription}
                disabled={aiDescriptionLoading || !form.brand || !form.model}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest text-gold border border-gold/40 hover:bg-gold hover:text-midnight rounded-sm transition-colors disabled:opacity-50"
                data-testid="generate-description-btn"
                title="Crea automaticamente una descrizione con AI"
              >
                <Wand2 size={12} className={aiDescriptionLoading ? "animate-pulse" : ""} />
                {aiDescriptionLoading ? "Genero..." : "Genera con AI"}
              </button>
            </div>
            <Textarea
              value={form.description}
              onChange={update("description")}
              rows={6}
              placeholder="Descrivi l'auto: optional, condizioni, storia..."
              className="bg-white/5 border-white/10 rounded-sm text-white"
              data-testid="form-description"
            />
            <p className="text-[10px] text-white/40 mt-2">
              Il pulsante "Genera con AI" scrive una descrizione commerciale basata sui dati inseriti. Puoi modificarla liberamente.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-sm font-semibold">Auto venduta</Label>
              <Switch checked={form.is_sold} onCheckedChange={(v) => setForm({ ...form, is_sold: v })} data-testid="form-sold" />
            </div>
            <p className="text-xs text-white/40 leading-relaxed mb-4">
              Se attivato, l'auto non compare nei risultati pubblici ma resta visibile qui.
            </p>
            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <Label className="text-sm font-semibold">Auto prenotata</Label>
              <Switch checked={form.is_reserved} onCheckedChange={(v) => setForm({ ...form, is_reserved: v })} data-testid="form-reserved" />
            </div>
            <p className="text-xs text-white/40 leading-relaxed mt-2">
              Contrassegna manualmente l'auto come prenotata (viene mostrata come tale ai clienti).
            </p>
          </div>

          <div className="bg-midnight-light border border-gold/40 p-6" data-testid="featured-block">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-gold rounded-full" />
                <Label className="text-sm font-semibold text-gold">Auto in Vetrina</Label>
              </div>
              <Switch
                checked={form.is_featured}
                onCheckedChange={(v) => setForm({ ...form, is_featured: v })}
                data-testid="form-featured"
              />
            </div>
            <p className="text-xs text-white/40 leading-relaxed">
              Se attivo, l'auto compare nello slideshow del hero in homepage.
              Consigliato: massimo 3-5 vetture, quelle con le foto più belle.
            </p>
            {form.is_featured && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <Label className="text-xs uppercase tracking-widest text-white/60">Posizione nello slideshow</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.featured_order}
                  onChange={update("featured_order")}
                  placeholder="0"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="form-featured-order"
                />
                <p className="text-[10px] text-white/40 mt-2">
                  Numero più basso = appare prima. Esempio: 1 = prima auto, 2 = seconda, ecc. In caso di parità viene mostrata prima l'auto aggiunta più di recente.
                </p>
              </div>
            )}
          </div>

          <div className="bg-midnight-light border border-gold/40 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-gold rounded-full" />
                <Label className="text-sm font-semibold text-gold">In promozione</Label>
              </div>
              <Switch
                checked={form.is_on_sale}
                onCheckedChange={(v) => setForm({ ...form, is_on_sale: v })}
                data-testid="form-on-sale"
              />
            </div>
            <p className="text-xs text-white/40 leading-relaxed mb-4">
              Se attivo, l'auto viene evidenziata nella sezione "Auto in Promozione" della homepage.
            </p>
            {form.is_on_sale && (
              <div className="space-y-4 pt-4 border-t border-white/5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60">Etichetta promo</Label>
                  <Input
                    value={form.sale_label}
                    onChange={update("sale_label")}
                    placeholder="Es. Offerta, Prezzo ribassato, Occasione"
                    className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                    data-testid="form-sale-label"
                  />
                  <p className="text-[10px] text-white/40 mt-1">Se vuoto, verrà mostrato "Promozione"</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60">Prezzo scontato (€)</Label>
                  <Input
                    type="number"
                    step="100"
                    value={form.sale_price}
                    onChange={update("sale_price")}
                    placeholder={form.price ? `Es. ${Math.round((parseFloat(form.price) || 0) * 0.9)}` : "Es. 12500"}
                    className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                    data-testid="form-sale-price"
                  />
                  <p className="text-[10px] text-white/40 mt-1">
                    Se compilato, il prezzo originale sarà mostrato barrato. Lascia vuoto per solo etichetta.
                  </p>
                </div>
              </div>
            )}
          </div>

          {!editing && (
            <div className="bg-midnight-light border border-white/5 p-5" data-testid="notify-panel">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.notify_customers}
                  onChange={(e) => setForm({ ...form, notify_customers: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-gold"
                  data-testid="notify-customers-checkbox"
                />
                <span>
                  <span className="block text-sm text-white">
                    Notifica clienti alla creazione
                  </span>
                  <span className="block text-[11px] text-white/50 mt-1 leading-relaxed">
                    Invia automaticamente una email agli iscritti alla newsletter e una notifica push
                    ai dispositivi che hanno installato la PWA. Le foto potranno essere aggiunte subito dopo.
                  </span>
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full flex items-center justify-center gap-2 py-4 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
            data-testid="save-car-btn"
          >
            <Save size={14} />
            {loading ? "Salvataggio..." : editing ? "Salva Modifiche" : "Crea Auto"}
          </button>

          {editing && (
            <div className="bg-midnight-light border border-white/5 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-gold">Foto ({images.length}/20)</h3>
              </div>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="image-upload-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || images.length >= 20}
                className="w-full border-2 border-dashed border-white/20 hover:border-gold hover:bg-white/5 transition-colors p-6 text-center rounded-sm mb-4 disabled:opacity-50"
                data-testid="upload-image-btn"
              >
                <ImagePlus size={22} className="text-gold mx-auto mb-2" />
                <p className="text-sm text-white/70">
                  {uploading ? "Caricamento..." : "Clicca per caricare"}
                </p>
                <p className="text-[10px] text-white/40 mt-1">JPG, PNG, WebP · Max 8MB</p>
              </button>

              {images.length > 0 && (
                <>
                  <p className="text-[11px] text-white/50 mb-2" data-testid="images-reorder-hint">
                    Trascina le foto per riordinarle. La prima diventa la copertina.
                  </p>
                  <div className="grid grid-cols-3 gap-2" data-testid="car-images-preview">
                    {images.map((img, idx) => (
                      <div
                        key={img.id}
                        className={`relative aspect-square group cursor-move border-2 ${idx === 0 ? "border-gold" : "border-transparent"} transition-colors`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", String(idx));
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                          const to = idx;
                          if (isNaN(from) || from === to) return;
                          const next = [...images];
                          const [moved] = next.splice(from, 1);
                          next.splice(to, 0, moved);
                          setImages(next);
                          try {
                            await api.post(`/cars/${carId}/images/reorder`, { order: next.map((i) => i.id) });
                            toast.success(idx === 0 ? "Nuova copertina impostata" : "Ordine aggiornato");
                          } catch (err) {
                            toast.error("Errore riordino foto");
                            setImages(images);
                          }
                        }}
                        data-testid={`car-image-${idx}`}
                      >
                        <img src={imageUrl(img.storage_path)} className="w-full h-full object-cover pointer-events-none" alt="" />
                        {idx === 0 && (
                          <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-gold text-midnight text-[9px] font-bold uppercase tracking-widest pointer-events-none">
                            Copertina
                          </span>
                        )}
                        <span className="absolute top-1 left-1 h-5 w-5 bg-midnight/85 text-gold text-[10px] font-bold flex items-center justify-center pointer-events-none">
                          {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeImage(img.id)}
                          className="absolute top-1 right-1 p-1 bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-image-${img.id}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {!editing && (
            <div className="bg-gold/10 border border-gold/30 p-4 text-sm text-white/80">
              Salva prima l'auto per poter caricare le foto.
            </div>
          )}

          {editing && waShareCar && (
            <button
              type="button"
              onClick={() => { setWaShareCar({ id: carId, ...form }); setWaShareOpen(true); }}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-[#25D366] hover:bg-[#1FB855] text-white text-xs uppercase tracking-widest font-bold transition-colors rounded-sm"
              data-testid="wa-broadcast-btn"
            >
              <MessageCircle size={15} strokeWidth={2.5} />
              Riprepara broadcast WhatsApp
            </button>
          )}
        </div>
      </form>

      <AdminWhatsappShareModal
        car={waShareCar}
        open={waShareOpen}
        onClose={() => setWaShareOpen(false)}
      />
    </AdminShell>
  );
}
